import { ChevronDown, Settings, User, Search, X } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import React, { useState, useMemo } from "react";
import { useNavigationPreferences } from "@/hooks/useNavigationPreferences";
import { COMPLETE_MENU_STRUCTURE, type MenuNode } from "@/config/menuStructure";
import { getMenuCategory, CATEGORY_ORDER, GETTING_STARTED_ORDER, type MenuCategory } from "@/config/menuCategories";
import { GlobalSearch } from "@/components/navigation/GlobalSearch";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useHostnameTheme } from "@/hooks/useHostnameTheme";
import { usePersonas } from "@/hooks/usePersonas";
import { useBrandManager } from "@/hooks/useBrandManager";
import { FrequentlyUsedSection } from "@/components/navigation/FrequentlyUsedSection";
import { useNavigationTracking } from "@/hooks/useNavigationTracking";
import { useMenuTutorialVideos } from "@/hooks/useMenuTutorialVideos";
import { MainMenuItemWithTutorial } from "@/components/navigation/MainMenuItemWithTutorial";
import { MainMenuItemPin } from "@/components/navigation/MainMenuItemPin";
import { useTypedTranslation } from "@/lib/i18n/hooks";
import type { NavigationKey } from "@/lib/i18n/types";
import { useCustomBrandFeatures } from "@/hooks/useCustomBrandFeatures";

export function DynamicAppSidebar() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { toggleSettingsSidebar } = useSettings();
  const { appName, logoUrl } = useHostnameTheme();
  const { preferences, isLoading: prefsLoading } = useNavigationPreferences();
  const { personas, isLoading: personasLoading } = usePersonas();
  const { currentBrandId } = useBrandManager();
  const { mainNavVideos } = useMenuTutorialVideos();
  const { t, tNav } = useTypedTranslation();
  const { hasFeature, isLoading: featuresLoading } = useCustomBrandFeatures();
  
  // Track navigation for auto-favorites
  useNavigationTracking();
  
  // State for menu search
  const [searchQuery, setSearchQuery] = useState('');
  
  // Map menu IDs to translation keys
  const getMenuTranslationKey = (menuId: string): string | null => {
    const translationMap: Record<string, string> = {
      'dashboard': 'dashboard',
      'newsfeed': 'newsfeed',
      'newsfeedStatic': 'newsfeedStatic',
      'start-here': 'startHere',
      'brand-management': 'brandManagement',
      'brand-setup': 'brandSetup',
      'brand-onboarding-questionnaire': 'brandOnboardingQuestionnaire',
      'brand-team': 'brandTeam',
      'brand-marktech': 'brandMarktech',
      'brand-products': 'brandProducts',
      'brand-moodboard': 'brandMoodboard',
      'brand-assets': 'brandAssets',
      'testimonials': 'testimonials',
      'client-logos': 'clientLogos',
      'product-enrichment': 'productEnrichment',
      'content-marketing': 'contentMarketing',
      'seo-machine': 'seoMachine',
      'seo-machine-dashboard': 'seoMachineDashboard',
      'articles': 'articles',
      'articles-list': 'articlesList',
      'article-categories': 'articleCategories',
      'article-tags': 'articleTags',
      'long-form-content': 'longFormContent',
      'human-driven-content': 'humanDrivenContent',
      'upload-content-ideas': 'uploadContentIdeas',
      'content-ideas': 'contentIdeas',
      'enhanced-content-ideas': 'enhancedContentIdeas',
      'cm-posts-suggested': 'cmPostsSuggested',
      'competitors-content': 'competitorsContent',
      'cm-posts-selected': 'cmPostsSelected',
      'reddit-subreddit-search': 'redditSubredditSearch',
      'seo-driven-content': 'seoDrivenContent',
      'getting-top-articles': 'gettingTopArticles',
      'getting-proper-structure': 'gettingProperStructure',
      'content-briefs': 'contentBriefs',
      'quality-checker': 'qualityChecker',
      'cm-quality-checker-outgoing': 'cmQualityCheckerOutgoing',
      'cm-quality-checker-incoming': 'cmQualityCheckerIncoming',
      'short-form-content': 'shortFormContent',
      'training-short-form': 'trainingShortForm',
      'short-form-content-pillars': 'shortFormContentPillars',
      'short-form-content-ideas': 'shortFormContentIdeas',
      'short-form-posts-suggested': 'shortFormPostsSuggested',
      'content-approval-dashboard': 'contentApprovalDashboard',
      'sm-posts-selected': 'smPostsSelected',
      'keydates': 'keydates',
      'creatives-art': 'creativesArt',
      'visual-inspiration': 'visualInspiration',
      'visual-generation': 'visualGeneration',
      'past-generations': 'pastGenerations',
      'creative-prompt-library': 'creativePromptLibrary',
      'newsroom': 'newsroom',
      'newsroom-dashboard': 'newsroomDashboard',
      'newsroom-programs': 'newsroomPrograms',
      'newsroom-sources': 'newsroomSources',
      'newsroom-collections': 'newsroomCollections',
      'newsroom-bulletins': 'newsroomBulletins',
      'newsroom-sponsorships': 'newsroomSponsorships',
      'newsfeed-dashboard': 'newsfeedDashboard',
      'newsfeed-sources': 'newsfeedSources',
      'newsfeed-collections': 'newsfeedCollections',
      'newsfeed-feeds': 'newsfeedFeeds',
      'newsfeed-threads': 'newsfeedThreads',
      'avatar-announcements': 'avatarAnnouncements',
      'avatar-announcements-list': 'avatarAnnouncementsList',
      'press-releases': 'pressReleases',
      'dispatch-center': 'dispatchCenter',
      'journalists': 'journalists',
      'distribution-lists': 'distributionLists',
      'podcasts': 'podcasts',
      'podcast-generations': 'podcastGenerations',
      'podcast-generation': 'podcastGeneration',
      'podcast-library': 'podcastLibrary',
      'interactive-polls': 'interactivePolls',
      'polls': 'polls',
      'poll-sources': 'pollSources',
      'poll-performance': 'pollPerformance',
      'poll-styles': 'pollStyles',
      'poll-templates': 'pollTemplates',
      'lead-magnets': 'leadMagnets',
      'interactive-quizzes': 'interactiveQuizzes',
      'quizzes': 'quizzes',
      'quiz-sources': 'quizSources',
      'quiz-performance': 'quizPerformance',
      'quiz-styles': 'quizStyles',
      'quiz-templates': 'quizTemplates',
      'interactive-mini-games': 'interactiveMiniGames',
      'mini-games': 'miniGames',
      'mini-game-templates': 'miniGameTemplates',
      'mini-game-sources': 'miniGameSources',
      'mini-game-performance': 'miniGamePerformance',
      'mini-game-types': 'miniGameTypes',
      'content-calendar': 'contentCalendar',
      'badges': 'badges',
      'badges-list': 'badgesList',
      'badge-users': 'badgeUsers',
      'badge-analytics': 'badgeAnalytics',
      'performance-marketing': 'performanceMarketing',
      'performance-marketing-dashboard': 'performanceMarketingDashboard',
      'utm-builder': 'utmBuilder',
      'google-ads': 'googleAds',
      'googleads-ads-analysis': 'googleadsAdsAnalysis',
      'googleads-ads-ai-insights': 'googleadsAdsAiInsights',
      'google-ads-processing': 'googleAdsProcessing',
      'google-ads-spend-by-product': 'googleAdsSpendByProduct',
      'linkedin-ads': 'linkedinAds',
      'linkedin-ads-analysis': 'linkedinAdsAnalysis',
      'facebook-ads': 'facebookAds',
      'facebook-ads-analysis': 'facebookAdsAnalysis',
      'social-media': 'socialMedia',
      'instagram': 'instagram',
      'sm-instagram-analysis': 'smInstagramAnalysis',
      'sm-instagram-get-feed': 'smInstagramGetFeed',
      'sm-instagram-profile-comparisons': 'smInstagramProfileComparisons',
      'linkedin': 'linkedin',
      'sm-competitors-list': 'smCompetitorsList',
      'social-media-channel-suggestion': 'socialMediaChannelSuggestion',
      'social-media-settings': 'socialMediaSettings',
      'seo': 'seo',
      'seo-dashboard': 'seoDashboard',
      'seo-testing': 'seoTesting',
      'seo-diagnostic': 'seoDiagnostic',
      'seo-trends-analysis': 'seoTrendsAnalysis',
      'serps-research': 'serpsResearch',
      'ai-overview-research': 'aiOverviewResearch',
      'keyword-explorer': 'keywordExplorer',
      'keyword-research': 'keywordResearch',
      'keyword-hopping': 'keywordHopping',
      'keyword-assistant': 'keywordAssistant',
      'keywords-5ws': 'keywords5ws',
      'competitors-keywords': 'competitorsKeywords',
      'on-site-seo': 'onSiteSeo',
      '3danalysis': '3danalysis',
      'historical-declines': 'historicalDeclines',
      'seo-selection-groups': 'seoSelectionGroups',
      'branded-keywords': 'brandedKeywords',
      'seo-internal-linking': 'seoInternalLinking',
      'seo-ai-insights': 'seoAiInsights',
      'seo-pages': 'seoPages',
      'seo-cannibalization': 'seoCannibalization',
      'seo-content-intelligence': 'seoContentIntelligence',
      'seo-analytics': 'seoAnalytics',
      'seo-keywords': 'seoKeywords',
      'seo-keyword-clusters': 'seoKeywordClusters',
      'seo-pagespeed': 'seoPagespeed',
      'seo-briefs': 'seoBriefs',
      'on-page-seo': 'onPageSeo',
      'onpage-seo-audit': 'onpageSeoAudit',
      'meta-generator': 'metaGenerator',
      'faq-generator': 'faqGenerator',
      'off-page-seo': 'offPageSeo',
      'competitors-backlinks': 'competitorsBacklinks',
      'backlink-intelligence': 'backlinkIntelligence',
      'youtube-seo': 'youtubeSeo',
      'youtube-seo-keywords': 'youtubeSeoKeywords',
      'youtube-seo-suggestions': 'youtubeSeoSuggestions',
      'local-seo': 'localSeo',
      'appstore-seo': 'appstoreSeo',
      'appstore-seo-keywords': 'appstoreSeoKeywords',
      'appstore-seo-suggestions': 'appstoreSeoSuggestions',
      'ecommerce-seo': 'ecommerceSeo',
      'saio': 'saio',
      'saio-dashboard': 'saioDashboard',
      'prompts': 'prompts',
      'run-analysis': 'runAnalysis',
      'scheduler': 'scheduler',
      'dynamic-competitor-classification': 'dynamicCompetitorClassification',
      'dynamic-people-classification': 'dynamicPeopleClassification',
      'ai-search-optimization-framework': 'aiSearchOptimizationFramework',
      'saio-data-exploration-tool': 'saioDataExplorationTool',
      'cro-ux': 'croUx',
      'cro-dashboard': 'croDashboard',
      'cro-website-suggestions': 'croWebsiteSuggestions',
      'fact-checker': 'factChecker',
      'fact-checker-analyze': 'factCheckerAnalyze',
      'my-fact-checks': 'myFactChecks',
      'synthetic-personas': 'syntheticPersonas',
      'personas-dashboard': 'personasDashboard',
      'personas-brief-settings': 'personasBriefSettings',
      'personas-brief-list': 'personasBriefList',
      'my-personas': 'myPersonas',
      'personas': 'personas',
      'recruiting-participants': 'recruitingParticipants',
      'participants': 'participants',
      'focus-groups': 'focusGroups',
      'fg-dashboard': 'fgDashboard',
      'projects': 'projects',
      'preparing-stimuli': 'preparingStimuli',
      'preparing-inputs': 'preparingInputs',
      'contacting-fg': 'contactingFg',
      'fg-results': 'fgResults',
      'planning-budgeting': 'planningBudgeting',
      'product-weights': 'productWeights',
      'planning-sheet': 'planningSheet',
      'digital-operations': 'digitalOperations',
      'task-planning': 'taskPlanning',
      'tasks': 'tasks',
      'scheduled-tasks': 'scheduledTasks',
      'tasks-aging': 'tasksAging',
      'tasks-journal': 'tasksJournal',
      'tasks-notifications': 'tasksNotifications',
      'sales-operations': 'salesOperations',
      'sales-proposals': 'salesProposals',
      'sales-moms': 'salesMoms',
      'msa-templates': 'msaTemplates',
      'msa-documents': 'msaDocuments',
      'supportive-templates': 'supportiveTemplates',
      'supportive-documents': 'supportiveDocuments',
      'sales-insights': 'salesInsights',
      'sops-templates': 'sopsTemplates',
      'sop-dashboard': 'sopDashboard',
      'sops': 'sops',
      'business-annotations': 'businessAnnotations',
      'reporting-hub': 'reportingHub',
      'business-activity': 'businessActivity',
      'business-metrics': 'businessMetrics',
      'email-marketing-report': 'emailMarketingReport',
      'google-impact': 'googleImpact',
      'causality-report': 'causalityReport',
      'seo-report': 'seoReport',
      'analytics': 'analytics',
      'predictive-analytics': 'predictiveAnalytics',
      'business-on-map': 'businessOnMap',
      'personal-cards': 'personalCards',
      'personal-cards-list': 'personalCardsList',
      'collaborations': 'collaborations',
      'collaboration-types': 'collaborationTypes',
      'collaborators': 'collaborators',
      'collaboration-campaigns': 'collaborationCampaigns',
      'growth-hacking': 'growthHacking',
      'growthhacking-about': 'growthhackingAbout',
      'gh-dashboard': 'ghDashboard',
      'gh-onepager': 'ghOnepager',
      'brainstorming-ideas': 'brainstormingIdeas',
      'prioritization': 'prioritization',
      'sprints': 'sprints',
      'growth-planning': 'growthPlanning',
      'growth-tasks': 'growthTasks',
      'growth-tasking': 'growthTasking',
      'growth-learnings': 'growthLearnings',
      'sales-channels': 'salesChannels',
      'gh-performance-insights': 'ghPerformanceInsights',
      'link-building': 'linkBuilding',
      'link-building-exchanges': 'linkBuildingExchanges',
      'link-building-analytics': 'linkBuildingAnalytics',
      'link-building-partners': 'linkBuildingPartners',
      'link-building-domains': 'linkBuildingDomains',
      'link-building-email-history': 'linkBuildingEmailHistory',
      'cold-outreach': 'coldOutreach',
      'cold-outreach-campaigns': 'coldOutreachCampaigns',
      'cold-outreach-lists': 'coldOutreachLists',
      'cold-outreach-contacts': 'coldOutreachContacts',
      'cold-outreach-dispatch': 'coldOutreachDispatch',
      'cold-outreach-analytics': 'coldOutreachAnalytics',
      'referral-system': 'referralSystem',
      'referral-contacts': 'referralContacts',
      'referral-programs': 'referralPrograms',
      'referral-lists': 'referralLists',
      'referral-campaigns': 'referralCampaigns',
      'referral-dispatch': 'referralDispatch',
      'referral-prompts': 'referralPrompts',
      'referral-analytics': 'referralAnalytics',
      'referral-email-analytics': 'referralEmailAnalytics',
      'referral-public-path': 'referralPublicPath',
      'referral-control-center': 'referralControlCenter',
      'tool-site-audit': 'toolSiteAudit',
      'siteaudit-dashboard': 'siteauditDashboard',
      'siteaudit-operations': 'siteauditOperations',
      'siteaudit-main-elements': 'siteauditMainElements',
      'siteaudit-open-graph': 'siteauditOpenGraph',
      'siteaudit-pixels-tags': 'siteauditPixelsTags',
      'siteaudit-marktech': 'siteauditMarktech',
      'siteaudit-speed-performance': 'siteauditSpeedPerformance',
      'siteaudit-mobile-analysis': 'siteauditMobileAnalysis',
      'siteaudit-expert-validation': 'siteauditExpertValidation',
      'siteaudit-webhook-settings': 'siteauditWebhookSettings',
      'documentation-hub': 'documentationHub',
      'doc-hub-dashboard': 'docHubDashboard',
      'doc-hub-events': 'docHubEvents',
      'doc-hub-documents': 'docHubDocuments',
      'doc-hub-tips': 'docHubTips',
      'doc-hub-snippets': 'docHubSnippets',
      'project-moms': 'projectMoms',
      'embeddable-widgets': 'embeddableWidgets',
      'public-chats': 'publicChats',
      'public-chat-widget-config': 'publicChatWidgetConfig',
      'public-chat-knowledge': 'publicChatKnowledge',
      'scale-2y3x': 'scale2y3x',
      'scale-2y3x-about': 'scale2y3xAbout',
      'scale-2y3x-dashboard': 'scale2y3xDashboard',
      'scale-2y3x-builder': 'scale2y3xBuilder',
      'scale-2y3x-goals': 'scale2y3xGoals',
      'scale-2y3x-growth-lab-team': 'scale2y3xGrowthLabTeam',
      'scale-2y3x-sprints': 'scale2y3xSprints',
      'scale-2y3x-ideas': 'scale2y3xIdeas',
      'scale-2y3x-task-planning': 'scale2y3xTaskPlanning',
      'scale-2y3x-tasks': 'scale2y3xTasks',
      'scale-2y3x-performance-insights': 'scale2y3xPerformanceInsights',
      'local-stores': 'localStores',
      'retail-data': 'retailData',
      'retail-store-data-loading': 'retailStoreDataLoading',
      'retail-store-performance': 'retailStorePerformance',
      'retail-store-micro-performance': 'retailStoreMicroPerformance',
      'retail-store-settings': 'retailStoreSettings',
      'learning-center': 'learningCenter',
      'lms-dashboard': 'lmsDashboard',
      'lms-coursemanagement': 'lmsCoursemanagement',
      'lms-instructors': 'lmsInstructors',
      'lms-course-types': 'lmsCourseTypes',
      'lms-enrollments': 'lmsEnrollments',
      'lms-company-enrollments': 'lmsCompanyEnrollments',
      'lms-analytics': 'lmsAnalytics',
      'digcomp': 'digcomp',
      'lms-quiz-management': 'lmsQuizManagement',
      'lms-certificate-templates': 'lmsCertificateTemplates',
      'lms-student-eligibility': 'lmsStudentEligibility',
      'lms-associations': 'lmsAssociations',
      'lms-academies': 'lmsAcademies',
      'lms-shares': 'lmsShares',
      'student-path': 'studentPath',
      'learn-auth': 'learnAuth',
      'learn-login': 'learnLogin',
      'learn-dashboard': 'learnDashboard',
      'learn-verify-certificate': 'learnVerifyCertificate',
      'public-path': 'publicPath',
      'public-academies': 'publicAcademies',
      'public-academy-detail': 'publicAcademyDetail',
      'public-academy-courses': 'publicAcademyCourses',
      'public-courses': 'publicCourses',
      'public-course-detail': 'publicCourseDetail',
      'course-checkout': 'courseCheckout',
      'payment-success': 'paymentSuccess',
      'payment-cancel': 'paymentCancel',
      'human-resources': 'humanResources',
      'hr-employees': 'hrEmployees',
      'hr-noteworthy-events': 'hrNoteworthyEvents',
      'hr-positions': 'hrPositions',
      'hr-org-chart': 'hrOrgChart',
      'hr-evaluation-categories': 'hrEvaluationCategories',
      'hr-evaluation-criteria': 'hrEvaluationCriteria',
      'hr-evaluations': 'hrEvaluations',
      'hr-job-posts': 'hrJobPosts',
      'internal-training': 'internalTraining',
      'internal-training-dashboard': 'internalTrainingDashboard',
      'internal-training-programs': 'internalTrainingPrograms',
      'internal-training-assignments': 'internalTrainingAssignments',
      'internal-training-progress': 'internalTrainingProgress',
      'internal-training-needs': 'internalTrainingNeeds',
      'internal-training-shares': 'internalTrainingShares',
      'surveys': 'surveys',
      'surveys-list': 'surveysList',
      'surveys-create': 'surveysCreate',
      'surveys-recipients': 'surveysRecipients',
      'surveys-submissions': 'surveysSubmissions',
      'surveys-process': 'surveysProcess',
      'surveys-results': 'surveysResults',
      'surveys-email-tracking': 'surveysEmailTracking',
      'surveys-email-management': 'surveysEmailManagement',
      'tools': 'tools',
      'time-gated-pdfs': 'timeGatedPdfs',
      'url-shortener': 'urlShortener',
      'incoming-transcripts': 'incomingTranscripts',
    };
    return translationMap[menuId] || null;
  };
  
  // Get translated label for a menu item
  const getTranslatedLabel = (item: MenuNode): string => {
    const translationKey = getMenuTranslationKey(item.id);
    if (translationKey) {
      try {
        // TypeScript will validate the key exists in navigation.json
        const translated = tNav(translationKey as NavigationKey);
        // If translation returns the key itself, it means translation is missing, use original label
        return translated !== translationKey ? translated : item.label;
      } catch {
        return item.label;
      }
    }
    return item.label;
  };

  // State for collapsible sections
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Check if an item or any of its children are visible based on preferences
  const isItemVisible = (itemId: string): boolean => {
    if (!preferences?.items) return true;
    const pref = preferences.items.find(p => p.id === itemId);
    return pref?.visible ?? true;
  };

  // Recursively check if any child items are visible
  const hasVisibleChildren = (item: MenuNode): boolean => {
    // If features are loading, hide items that require features
    if (item.featureKey && featuresLoading) {
      return false;
    }
    
    // Check if parent item requires a feature
    if (item.featureKey && !hasFeature(item.featureKey as any)) {
      return false;
    }

    if (!item.children || item.children.length === 0) {
      return isItemVisible(item.id);
    }
    
    return item.children.some(child => {
      // If features are loading, hide items that require features
      if (child.featureKey && featuresLoading) {
        return false;
      }
      
      // Check if child requires a feature
      if (child.featureKey && !hasFeature(child.featureKey as any)) {
        return false;
      }
      return isItemVisible(child.id) && hasVisibleChildren(child);
    });
  };

  // Precompute default order for top-level menu items
  const defaultTopLevelOrder = useMemo(() => {
    return new Map(COMPLETE_MENU_STRUCTURE.map((item, idx) => [item.id, idx]));
  }, []);

  // Get item order from preferences with sensible fallback
  const getItemOrder = (itemId: string): number => {
    if (!preferences?.items) return defaultTopLevelOrder.get(itemId) ?? 0;
    const pref = preferences.items.find(p => p.id === itemId);
    if (typeof pref?.order === 'number') return pref.order;
    return defaultTopLevelOrder.get(itemId) ?? 0;
  };

  // Sort items by order preference
  const sortByOrder = (items: MenuNode[]): MenuNode[] => {
    return [...items].sort((a, b) => getItemOrder(a.id) - getItemOrder(b.id));
  };

  // Sort by category then by user order within category (Getting Started uses fixed order: Today's News, Dashboard, Start here)
  const sortByCategoryThenOrder = (items: MenuNode[]): MenuNode[] => {
    return [...items].sort((a, b) => {
      const catA = getMenuCategory(a.id);
      const catB = getMenuCategory(b.id);
      const orderA = CATEGORY_ORDER.indexOf(catA);
      const orderB = CATEGORY_ORDER.indexOf(catB);
      if (orderA !== orderB) return orderA - orderB;
      if (catA === "Getting Started") {
        const iA = GETTING_STARTED_ORDER.indexOf(a.id);
        const iB = GETTING_STARTED_ORDER.indexOf(b.id);
        return (iA === -1 ? 999 : iA) - (iB === -1 ? 999 : iB);
      }
      return getItemOrder(a.id) - getItemOrder(b.id);
    });
  };

  // Check if item would be rendered (same logic as renderMenuItem early returns)
  const wouldMenuItemRender = (item: MenuNode): boolean => {
    if (item.featureKey) {
      if (featuresLoading) return false;
      if (!hasFeature(item.featureKey as any)) return false;
    }
    if (item.children && item.children.length > 0) {
      if (!hasVisibleChildren(item)) return false;
    } else {
      if (!isItemVisible(item.id)) return false;
    }
    return true;
  };

  // Filter menu items recursively based on search query
  const filterMenuItems = (items: MenuNode[], query: string): MenuNode[] => {
    if (!query.trim()) return items;
    
    const lowerQuery = query.toLowerCase();
    
    return items.map(item => {
      // Get translated label for matching
      const translatedLabel = getTranslatedLabel(item);
      
      // Check if item matches
      const labelMatch = translatedLabel?.toLowerCase().includes(lowerQuery);
      const pathMatch = item.path?.toLowerCase().includes(lowerQuery);
      const descriptionMatch = item.description?.toLowerCase().includes(lowerQuery);
      const itemMatches = labelMatch || pathMatch || descriptionMatch;
      
      // If item has children, filter them recursively
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterMenuItems(item.children, query);
        // Include parent if it matches OR if any child matches
        if (itemMatches || filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren
          };
        }
        // Return null to filter out this item
        return null;
      }
      
      // For leaf items, return item if it matches, null otherwise
      return itemMatches ? item : null;
    }).filter((item): item is MenuNode => item !== null);
  };

  // Filter menu structure based on search query
  const filteredMenuStructure = useMemo(() => {
    if (!searchQuery.trim()) return COMPLETE_MENU_STRUCTURE;
    return filterMenuItems(COMPLETE_MENU_STRUCTURE, searchQuery);
  }, [searchQuery]);

  // Sort by category then user order (same groups as Settings: Marketing, Sales, Operations, Admin, Systems)
  const sortedMenuStructure = useMemo(
    () => sortByCategoryThenOrder(filteredMenuStructure),
    [filteredMenuStructure, preferences?.items]
  );

  // First visible item id per category (so we show category separator only before the first item in each group)
  const firstVisibleInCategory: Partial<Record<MenuCategory, string>> = {};
  for (const item of sortedMenuStructure) {
    if (!wouldMenuItemRender(item)) continue;
    const cat = getMenuCategory(item.id);
    if (firstVisibleInCategory[cat] === undefined) firstVisibleInCategory[cat] = item.id;
  }

  // Map raw category to translation key
  const getCategoryTranslationKey = (cat: MenuCategory): string => {
    const map: Record<MenuCategory, string> = {
      'Getting Started': 'categoryGettingStarted',
      'Marketing': 'categoryMarketing',
      'Sales': 'categorySales',
      'Operations': 'categoryOperations',
      'Systems': 'categorySystems',
    };
    return map[cat] ?? cat;
  };

  const CategorySeparator = ({ label }: { label: string }) => (
    <div className="flex items-center gap-2 px-2 py-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-[12px] shrink-0 flex-1 h-px bg-muted-foreground/50" />
    </div>
  );

  // Render menu items recursively
  const renderMenuItem = (item: MenuNode, level: number = 0): JSX.Element | null => {
    // Check if item requires a feature and if the feature is enabled
    // If features are still loading, hide items that require features to prevent flickering
    if (item.featureKey) {
      if (featuresLoading) return null; // Hide while loading to prevent showing wrong state
      if (!hasFeature(item.featureKey as any)) return null;
    }

    // For parent items with children, check if any children are visible
    if (item.children && item.children.length > 0) {
      if (!hasVisibleChildren(item)) return null;
    } else {
      // For leaf items, check direct visibility
      if (!isItemVisible(item.id)) return null;
    }

    const hasChildren = item.children && item.children.length > 0;
    // Only mark as active if path matches exactly, or if path is a prefix and we're on a child route
    // But don't mark as active if multiple items share the same path
    const isActive = item.path 
      ? (location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path + '/')))
      : false;
    const Icon = item.icon;

    // Helper to render icon (handles both Lucide components and image paths)
    const renderIcon = () => {
      if (!Icon) return null;
      // If Icon is a string (image path), render as img
      if (typeof Icon === 'string') {
        return <img src={Icon} alt="" className="h-4 w-4 object-contain" />;
      }
      // Otherwise render as Lucide component
      return <Icon className="h-4 w-4 text-primary" />;
    };

    // If item has a path and no children, it's a direct link
    if (item.path && !hasChildren) {
      // Append brand_id to public course paths and learn login
      const needsBrandId = (item.path.startsWith('/courses') || item.path === '/learn/login') && currentBrandId;
      const linkPath = needsBrandId
        ? `${item.path}${item.path.includes('?') ? '&' : '?'}brand_id=${currentBrandId}`
        : item.path;

      return (
        <MainMenuItemWithTutorial
          key={item.id}
          id={item.id}
          label={getTranslatedLabel(item)}
          path={linkPath}
          icon={Icon}
          isActive={isActive}
          level={level}
          tutorialVideo={mainNavVideos[item.id]}
          additionalIcons={<MainMenuItemPin path={linkPath} />}
        />
      );
    }

    // If item has no path but no children, make it navigate to parent or first child
    if (!item.path && !hasChildren) {
      // For survey items without paths, navigate to /surveys but don't mark as active
      const fallbackPath = '/surveys';
      return (
        <MainMenuItemWithTutorial
          key={item.id}
          id={item.id}
          label={getTranslatedLabel(item)}
          path={fallbackPath}
          icon={Icon}
          isActive={false}
          level={level}
          tutorialVideo={mainNavVideos[item.id]}
          additionalIcons={<MainMenuItemPin path={fallbackPath} />}
        />
      );
    }

    // If item has children, it's a collapsible section
    if (hasChildren) {
      const visibleChildren = sortByOrder(item.children!).filter(child => isItemVisible(child.id));
      if (visibleChildren.length === 0) return null;

      // Generate padding class based on level for consistent tree visualization
      const getLevelPadding = () => {
        switch (level) {
          case 0: return '';
          case 1: return 'pl-5';
          case 2: return 'pl-10';
          case 3: return 'pl-[60px]';
          default: return 'pl-20';
        }
      };

      return (
        <Collapsible 
          key={item.id}
          open={openSections[item.id]} 
          onOpenChange={() => toggleSection(item.id)}
        >
          <SidebarGroup className="px-0 py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${getLevelPadding()} ${openSections[item.id] ? 'bg-accent text-accent-foreground' : ''}`}>
                <div className="flex items-center gap-2">
                  {renderIcon()}
                  <span>{getTranslatedLabel(item)}</span>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform text-primary ${openSections[item.id] ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleChildren.map(child => renderMenuItem(child, level + 1))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      );
    }

    return null;
  };

  // Group personas by brief
  const personasByBrief = personas.reduce((acc, persona) => {
    const briefName = persona.brief?.name || 'Uncategorized';
    if (!acc[briefName]) {
      acc[briefName] = [];
    }
    acc[briefName].push(persona);
    return acc;
  }, {} as Record<string, typeof personas>);

  return (
    <Sidebar>
      <SidebarHeader className="sticky top-0 z-10 bg-background border-b">
        <div className="p-2 flex flex-col items-center space-y-3">
          <img 
            src={logoUrl || 'https://hjodtxoylcvcwjgvuctk.supabase.co/storage/v1/object/public/logos/transparent-logo.png'} 
            alt={`${appName} Logo`} 
            className="w-12 h-12 rounded-lg object-contain"
          />
          <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent text-center">
            {appName}
          </h1>
        </div>
        
        {/* Search Bar */}
        <div className="px-2 pb-2 w-full">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={tNav('searchMenuPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-8 h-9 text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        
        {isMobile && (
          <div className="px-2 pb-2">
            <GlobalSearch />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="gap-0">
        {/* Frequently Used Section - auto-favorites based on usage */}
        <FrequentlyUsedSection />
        
        {/* Render all menu items from filtered menu structure */}
        {prefsLoading ? (
          <div className="p-4 text-center text-muted-foreground">Loading...</div>
        ) : searchQuery.trim() && filteredMenuStructure.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            No menu items found matching "{searchQuery}"
          </div>
        ) : (
          sortedMenuStructure.map(item => {
            const rendered = renderMenuItem(item);
            if (rendered == null) return null;
            const cat = getMenuCategory(item.id);
            const isFirstInCategory = firstVisibleInCategory[cat] === item.id;
            return isFirstInCategory ? (
              <div key={item.id}>
                <CategorySeparator label={tNav(getCategoryTranslationKey(cat) as NavigationKey)} />
                {rendered}
              </div>
            ) : (
              <div key={item.id}>{rendered}</div>
            );
          })
        )}

        {/* Personas Section - dynamic based on user data */}
        {!personasLoading && personas.length > 0 && (
          <Collapsible open={openSections.personas} onOpenChange={() => toggleSection('personas')}>
            <SidebarGroup className="px-0 py-2">
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.personas ? 'bg-accent text-accent-foreground' : ''}`}>
                  Personas
                  <ChevronDown className={`h-4 w-4 transition-transform text-primary ${openSections.personas ? 'rotate-180' : ''}`} />
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {Object.entries(personasByBrief).map(([briefName, briefPersonas]) => (
                      <div key={briefName}>
                        <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                          {briefName}
                        </div>
                        {briefPersonas.map((persona) => (
                          <SidebarMenuItem key={persona.id}>
                            <SidebarMenuButton 
                              asChild 
                              isActive={location.pathname === `/personas/${persona.id}`}
                              tooltip={persona.name}
                            >
                              <Link to={`/personas/${persona.id}`} className="w-full pl-6">
                                <User className="h-4 w-4 text-primary" />
                                <span className="truncate">{persona.name}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </div>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}
      </SidebarContent>
      
      <SidebarFooter className="sticky bottom-0 bg-background border-t p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSettingsSidebar}
          className="w-full justify-start gap-2 h-8"
        >
          <Settings className="h-4 w-4 text-primary" />
          <span>Settings</span>
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
